/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import Text from "../ui/Text";
import Icon from "../ui/Icon";
import Anchor from "../ui/Anchor";
import TextCircleDivider from "../ui/TextCircleDivider";
import FormatDateTime from "../formatters/FormatDateTime";
import FormatDatePeriod from "../formatters/FormatDatePeriod";
import FormatPrice from "../formatters/FormatPrice";
import FormatDuration from "../formatters/FormatDuration";
import { useAssetsSrc } from "../../hooks/useAssetsSrc";
import { getBookingProducts } from "../../utils/getBookingProducts";
import T from "../ui/Translation";
import LineItem from "./LineItem";
import TreeView from "./tree/TreeView";
import TreeItem from "./tree/TreeItem";
import TreeItemChilds from "./tree/TreeItemChilds";
import TreeChild from "./tree/TreeChild";

const Card = styled.section`
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 0.5rem;
  overflow: hidden;
`;

const Row = styled(Flex)`
  padding: 1rem;
`;

const HorizontalDivider = styled.div`
  margin: 0 1rem;
  height: 1px;
  background: rgba(${({ theme }) => theme.colors.border.rgba});
`;

const ScheduledHeaderRow = styled(Flex)`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.05);
`;

const IconBadge = styled(Flex)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(${({ theme }) => theme.colors.background.rgba});
  flex-shrink: 0;
`;

const AvatarFrame = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(${({ theme }) => theme.colors.iconStroke.rgba});
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ServiceTreeView = styled(TreeView)`
  padding: 1rem;
`;

const ServiceTreeItem = styled(TreeItem)`
  padding: 0;
  gap: 1rem;
`;

const ProductTreeChilds = styled(TreeItemChilds)`
  padding-left: 0.5rem;
`;

const EntityAvatar = ({ picturePath, alt, fallback }) => {
  const src = useAssetsSrc(picturePath);
  return (
    <AvatarFrame>
      {src ? <AvatarImage src={src} alt={alt} /> : fallback}
    </AvatarFrame>
  );
};

const ScheduledHeader = ({ startDatetime, endDatetime, duration }) => (
  <ScheduledHeaderRow $align="center" $gap="1rem">
    <IconBadge $align="center" $justify="center">
      <Icon.CalendarCheck size={Icon.SIZE.MD} color="primary" />
    </IconBadge>
    <Stack $gap="0.25rem">
      <Text size="small" color="dark">Currently scheduled</Text>
      <Stack $gap="0">
        <Text color="dark" weight="bold">
          <FormatDateTime value={startDatetime} format={FormatDateTime.FORMAT.DATE} />,{" "}
          <FormatDatePeriod from={startDatetime} to={endDatetime} format={FormatDatePeriod.FORMAT.TIME} />
        </Text>
        <Text size="small">
          <T>Duration</T>: <FormatDuration value={duration} type={FormatDuration.TYPE.HOUR} />
        </Text>
      </Stack>
    </Stack>
  </ScheduledHeaderRow>
);

const ProviderRow = ({ provider }) => (
  <Row $align="center" $gap="1rem">
    <EntityAvatar
      picturePath={provider.picture_preview}
      alt={provider.name}
      fallback={<Icon.User size={Icon.SIZE.SM} color="iconStroke" />}
    />
    <Stack $gap="0.25rem">
      <Text color="dark" weight="bold">{provider.name}</Text>
      <Text size="small">Provider</Text>
    </Stack>
  </Row>
);

const LocationRow = ({ location }) => {
  const hasMapLink = location.lat && location.lng;
  const nameNode = <Text color="dark" weight="bold">{location.name}</Text>;

  return (
    <Row $align="center" $gap="1rem">
      <EntityAvatar
        picturePath={location.picture_preview}
        alt={location.name}
        fallback={<Icon.LocationPlaceholderIcon size={Icon.SIZE.SM} strokeWidth={3} color="iconStroke" />}
      />
      <Stack $gap="0.25rem">
        {hasMapLink ? (
          <Anchor
            underline="always"
            to={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {nameNode}
          </Anchor>
        ) : (
          nameNode
        )}
        {location.full_address && <Text size="small">{location.full_address}</Text>}
      </Stack>
    </Row>
  );
};

const ServiceMeta = ({ duration, price, currency }) => {
  const hasDuration = duration > 0;
  const hasPrice = price > 0;
  if (!hasDuration && !hasPrice) return null;

  return (
    <Flex $align="center" $gap="0.25rem">
      {hasDuration && (
        <Text size="small">
          <FormatDuration value={duration} type={FormatDuration.TYPE.HOUR} />
        </Text>
      )}
      {hasDuration && hasPrice && <TextCircleDivider />}
      {hasPrice && (
        <Text size="small" color="dark" weight="bold">
          <FormatPrice value={price} currency={currency} />
        </Text>
      )}
    </Flex>
  );
};

const getProductName = (product) => (product.product ? product.product.name : product.name);
const getProductPrice = (product) =>
  product.product
    ? Number(product.amount || 0)
    : Number(product.price || 0) * Number(product.qty || 0);

const ProductTree = ({ products, fallbackCurrency }) => (
  <ProductTreeChilds>
    {products.map((product, idx) => {
      const price = getProductPrice(product);

      return (
        <TreeChild key={product.id ?? idx} $isLast={idx === products.length - 1}>
          <LineItem quantity={product.qty} name={getProductName(product)} size="small" />
          {price > 0 && (
            <Text color="dark" weight="bold" size="small">
              <FormatPrice value={price} currency={product.currency || fallbackCurrency} />
            </Text>
          )}
        </TreeChild>
      );
    })}
  </ProductTreeChilds>
);

const ServiceRow = ({ service, products }) => (
  <ServiceTreeView>
    <ServiceTreeItem>
      <Flex $align="center" $gap="1rem">
        <EntityAvatar
          picturePath={service.picture_preview}
          alt={service.name}
          fallback={<Icon.SelectServices size={Icon.SIZE.SM} />}
        />
        <Stack $gap="0.25rem">
          <Text color="dark" weight="bold">{service.name}</Text>
          <ServiceMeta
            duration={service.duration || 0}
            price={Number(service.price || 0)}
            currency={service.currency}
          />
        </Stack>
      </Flex>
      {products.length > 0 && <ProductTree products={products} fallbackCurrency={service.currency} />}
    </ServiceTreeItem>
  </ServiceTreeView>
);

const RescheduleBookingSummary = ({ booking, batchInfo }) => {
  const products = getBookingProducts(booking);
  const hasLocation = booking.location && !Array.isArray(booking.location);

  const sections = [
    hasLocation && <LocationRow location={booking.location} />,
    booking.provider && <ProviderRow provider={booking.provider} />,
    booking.service && <ServiceRow service={booking.service} products={products} />,
  ].filter(Boolean);

  return (
    <Card>
      <ScheduledHeader
        startDatetime={batchInfo?.startDatetime || booking.start_datetime}
        endDatetime={batchInfo?.endDatetime || booking.end_datetime}
        duration={batchInfo?.duration ?? (booking.service?.duration || 0)}
      />

      {sections.map((section, i) => (
        <React.Fragment key={i}>
          {i > 0 && <HorizontalDivider />}
          {section}
        </React.Fragment>
      ))}
    </Card>
  );
};

export default RescheduleBookingSummary;
